/**
 * Formatting for the UI edge only. The API always speaks integer centavos,
 * basis points and ISO 8601 dates.
 */
export const TIME_ZONE = 'America/Sao_Paulo';

const brl = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

/** 124990 → "R$ 1.249,90" (with a normal space, not NBSP). */
export function formatBRL(centavos: number): string {
  return brl.format(centavos / 100).replace(/ /g, ' ');
}

/** 1400 → "14%" ; 1250 → "12,5%" */
export function formatBps(bps: number): string {
  return `${new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 2 }).format(bps / 100)}%`;
}

function parts(date: Date | string): Record<string, string> {
  const d = typeof date === 'string' ? new Date(date) : date;
  const out: Record<string, string> = {};
  for (const p of new Intl.DateTimeFormat('pt-BR', {
    timeZone: TIME_ZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(d)) {
    out[p.type] = p.value;
  }
  return out;
}

/** "06/10/2026" */
export function formatDate(date: Date | string): string {
  const p = parts(date);
  return `${p.day}/${p.month}/${p.year}`;
}

/** "14h30" */
export function formatTime(date: Date | string): string {
  const p = parts(date);
  return `${p.hour}h${p.minute}`;
}

/** "06/10/2026 14h30" */
export function formatDateTime(date: Date | string): string {
  return `${formatDate(date)} ${formatTime(date)}`;
}

/** Calendar date in São Paulo as `YYYY-MM-DD` (fee versions and other effective dates). */
export function isoDateInSaoPaulo(date: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: TIME_ZONE }).format(date);
}

/** `2026-11-01` → `01/11/2026` (no timezone shift: it is already a calendar date). */
export function formatIsoDate(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

/** Adds days to a `YYYY-MM-DD` calendar date. */
export function addDaysIso(iso: string, days: number): string {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** `YYYY-MM` of a calendar date or instant (São Paulo). */
export function monthOf(date: string | Date = new Date()): string {
  const iso = typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date) ? date : null;
  return (iso ?? isoDateInSaoPaulo(typeof date === 'string' ? new Date(date) : date)).slice(0, 7);
}

/** Adds months to `YYYY-MM`. */
export function addMonths(month: string, n: number): string {
  const [y, m] = month.split('-').map(Number);
  const total = y! * 12 + (m! - 1) + n;
  return `${Math.floor(total / 12)}-${String((total % 12) + 1).padStart(2, '0')}`;
}

/** First and last calendar day of `YYYY-MM`. */
export function monthBounds(month: string): { first: string; last: string; days: number } {
  const [y, m] = month.split('-').map(Number);
  const days = new Date(Date.UTC(y!, m, 0)).getUTCDate();
  return { first: `${month}-01`, last: `${month}-${String(days).padStart(2, '0')}`, days };
}

/** Adds months to a `YYYY-MM-DD`, keeping the day (clamped to the month's last day). */
export function addMonthsIso(iso: string, n: number): string {
  const month = addMonths(iso.slice(0, 7), n);
  const day = Math.min(Number(iso.slice(8, 10)), monthBounds(month).days);
  return `${month}-${String(day).padStart(2, '0')}`;
}

export const MONTH_NAMES = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
] as const;

/** `2026-10` → "Outubro 2026" (or "outubro" with `{ year: false, capital: false }`). */
export function monthLabel(
  month: string,
  opts: { year?: boolean; capital?: boolean } = {},
): string {
  const name = MONTH_NAMES[Number(month.slice(5, 7)) - 1] ?? month;
  const text = opts.capital === false ? name : name[0]!.toUpperCase() + name.slice(1);
  return opts.year === false ? text : `${text} ${month.slice(0, 4)}`;
}
